import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  ProgramPurpose,
  ValidationSubmissionConfiguration
} from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { getDate } from '@percent/admin-dashboard/common/utility/getDate'
import { Badge, Flex, Icon, Menu, TableCell, TableRow } from '@percent/lemonade'
import style from './Programs.module.scss'
import { DuplicateModal } from './modals/DuplicateModal'
import { PurposeModal } from './modals/PurposeModal'
import { UpdateStatusModal } from './modals/UpdateStatusModal'

type ProgramRowProps = {
  row: ValidationSubmissionConfiguration
  refreshPrograms: VoidFunction
}

export function ProgramRow({ row, refreshPrograms }: ProgramRowProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const addNewProgramFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_ADD_NEW_PROGRAM)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false)
  const [selectedPurposes, setSelectedPurposes] = useState<ProgramPurpose[] | null>(null)

  const handleMenuSelect = (key: string) => {
    if (
      !key.includes('duplicate-program') &&
      !key.includes('mark-program-as-') &&
      !key.includes('set-program-purpose')
    ) {
      navigate(key)
    }
  }

  const sections = [
    {
      items: [
        {
          key: `/partners/${row.partnerId}/programs/${row.id}/details`,
          label: t('drawer.configuration.programs.seeDetails')
        },
        {
          key: `/partners/${row.partnerId}/programs/${row.id}/eligibility`,
          label: addNewProgramFeatureFlag
            ? t('typography.editEligibilityCriteria')
            : t('typography.seeEligibilityCriteria'),
          shouldBeDisabled: !row?.configuration?.checks?.some(check => check?.type === 'eligibility')
        },
        ...(addNewProgramFeatureFlag
          ? [
              {
                key: `set-program-purpose-${row.id}`,
                label: t('menu.setProgramPurpose'),
                onClick: () => setSelectedPurposes(row.purposes ?? [])
              },
              {
                key: `/partners/${row.partnerId}/programs/${row.id}/duplicate-program`,
                label: t('typography.duplicateProgram'),
                onClick: () => {
                  setIsDuplicateModalOpen(true)
                }
              },
              ...(row.deprecatedAt
                ? [
                    {
                      key: `mark-program-as-active-${row.id}`,
                      label: t('menu.markProgramAsActive'),
                      onClick: () => setIsUpdateStatusModalOpen(true)
                    }
                  ]
                : [
                    {
                      key: `mark-program-as-deprecated-${row.id}`,
                      label: t('menu.markProgramAsDeprecated'),
                      onClick: () => setIsUpdateStatusModalOpen(true)
                    }
                  ])
            ]
          : [])
      ]
    }
  ]

  const renderPurposeCell = (purposes?: ProgramPurpose[] | null) => {
    if (!purposes || purposes.length === 0) {
      return t('programPurpose.notAvailable')
    }

    return (
      <Flex direction="column" gap="xs" align="flex-start">
        {purposes.map(purpose => (
          <Badge key={purpose} variant="default">
            {t(`programPurpose.${purpose}`)}
          </Badge>
        ))}
      </Flex>
    )
  }

  return (
    <>
      <TableRow className={style.tableRow} key={row.id}>
        <TableCell>{row.name || t('typography.unnamedProgram')}</TableCell>
        <TableCell>
          {row.deprecatedAt ? (
            <Badge variant="default">{t('status.deprecated')}</Badge>
          ) : (
            <Badge variant="positive">{t('status.active')}</Badge>
          )}
        </TableCell>
        <TableCell>{renderPurposeCell(row.purposes)}</TableCell>
        <TableCell>{getDate(row.lastUsedAt) ?? t('status.notAvailable')}</TableCell>
        <TableCell>
          <Menu
            sections={sections}
            customTriggerComponent={
              <span role="button" tabIndex={0} style={{ cursor: 'pointer', pointerEvents: 'auto' }}>
                <Icon name="menu-horizontal" size="m" color="gray300" />
              </span>
            }
            onSelect={handleMenuSelect}
          />
        </TableCell>
      </TableRow>
      <PurposeModal
        program={row}
        selectedPurposes={selectedPurposes}
        setSelectedPurposes={setSelectedPurposes}
        refreshPrograms={refreshPrograms}
      />
      <DuplicateModal program={row} isModalOpen={isDuplicateModalOpen} setIsModalOpen={setIsDuplicateModalOpen} />
      <UpdateStatusModal
        program={row}
        isModalOpen={isUpdateStatusModalOpen}
        setIsModalOpen={setIsUpdateStatusModalOpen}
        refreshPrograms={refreshPrograms}
      />
    </>
  )
}
